import {state as appState} from "../store/modules/app";
import {actions as userActions} from "@/store/modules/user";
import {state as projectState} from "../store/modules/project";
import {$t} from "@/i18n";
import {
    formatNumber,
    formatBool,
    formatDate,
    formatChoiceValue,
    formatMultiChoiceValue,
    formatDateFriendlyNoYear, parseSysDate, formatDateCustom, CustomFormats
} from '../plugins/formatting'
import {get_language_from_code, get_country_from_code} from "../helpers/formatting";

export const getCustomFieldsByRelatedType = (accountId, relatedTypes) => {
    const accountCustomFields = appState.custom_fields[accountId]
    if (accountCustomFields === undefined) {
      return [];
    }

    const customFields = accountCustomFields.filter(field => {
        return relatedTypes.some(relatedType => field.related_type.startsWith(relatedType))
    })

    const sortedCustomFields = customFields.sort((a, b) => {
        if (a.sort_order < b.sort_order) return -1;
        if (a.sort_order > b.sort_order) return 1;
        return 0;
    })

    if (!sortedCustomFields || !sortedCustomFields.length) {
        return []
    }
    return sortedCustomFields
};

export const getFieldComponent = (field) => {
    switch (field.type) {
        case "url":
        case "text":
        case "integer":
        case "number":
            return () => import("../components/CustomField/CustomTextField");
        case "choice_single":
        case "choice_multi":
        case "project_status":
        case "client_status":
            return () => import("../components/CustomField/CustomSelectField");
        case "user_single":
        case "user_multi":
            return () => import("../components/CustomField/UserField");
        case "cp_menu_links":
            return () => import("../components/CustomField/CpMenuLinks")
        case "article":
            console.error("Missing component for ", field.type);
            return null;
        case "client_groups":
        case "language":
        case "tax_id":
        case "country_code":
            return () => import("../components/CustomField/ClassifierChoiceField");
        case "date":
            return () => import("../components/CustomField/DateField");
        case "boolean":
            return () => import("../components/CustomField/BooleanField");
        case "note":
            return () => import("../components/CustomField/NoteField");
        case "contacts":
            return () => import("../components/CustomField/ContactsField/Index");
        case "emails":
            return () => import("../components/CustomField/EmailsField/Index");
        case "email":
            return () => import("../components/CustomField/CustomTextField");
        case "color":
            return () => import("../components/CustomField/ColorField");
        case "client":
            return () => import("../components/CustomField/ClientField");
        case "cp_rights":
            return () => import("../components/CustomField/CpRights.vue");
        case "roles":
            return () => import("../components/CustomField/RolesField.vue");
        case "cloud_folder":
            return () => import("../components/CustomField/FolderSelectField.vue")
        default:
            if (field.type.startsWith("supplier."))
                return () => import("../components/CustomField/SupplierField");
            if (field.type.startsWith("suppliers."))
                return () => import("../components/CustomField/SupplierTableField");

            console.error("Missing component for ", field.type);
            return null;
    }
};

export const getRequiredClassifierByField = (field) => {
    switch (field.type) {
        case "user_single":
        case "user_multi":
            return null;
        case "article":
            console.error("Missing classifier for article");
            return null;
        case "client_groups":
            return "client_groups"
        case "language":
            return "languages";
        case "country_code":
            return "countries";
        case "client":
            return "clients";
        default:
            return null;
    }
};

export const getFieldSizeCssClass = (field, columnsCount) => {
    switch (field.type) {
        case "contacts":
        case "note":
        case "emails":
        case "cp_menu_links":
        case "cloud_folder":
            return 'col-12';
        default:
            if (field.type.startsWith("suppliers."))
                return 'col-12';
            return 'col-sm-{0} col-12'.format(12 / columnsCount);
    }
};

export const getActionsByFields = (group_fields) => {
    let ret = [];
    for (let group_field of group_fields) {
        if (group_field.type === "contacts" || group_field.type.startsWith("suppliers.") || group_field.type === "emails") {
            ret.push({
                field_name: group_field.name,
                action: "add",
                icon: "mdi-plus",
                text: $t("Add")
            });
        }
    }
    return ret;
};

export const formatProjectStatus = (project, field) => {
    if ((project.status_bit & projectState.project_status.archived) === projectState.project_status.archived)
        return $t("Archived");
    if ((project.status_bit & projectState.project_status.cancelled) === projectState.project_status.cancelled)
        return $t("Cancelled");
    return project.status == null ? $t("Active") : formatChoiceValue(field, project.status);
}

export const formatClientStatus = (client, field) => {
    switch (client.status) {
        case "active":
            return $t("Active");
        case "inactive":
            return $t("Inactive");
        default:
            return client.status;
    }
}

export const getFieldFormatByType = (field_type) => {
    switch (field_type) {
        case "text":
        case "note":
            return (item, col) => item[col.value] == null ? "" : item[col.value];
        case "integer":
            return (item, col) => item[col.value] == null ? "" : formatNumber(item[col.value], 0, 0);
        case "number":
            return (item, col) => item[col.value] == null ? "" : formatNumber(item[col.value]);
        case "boolean":
            return (item, col) => item[col.value] == null ? "" : formatBool(item[col.value]);
        case "date":
            return (item, col) => item[col.value] == null ? "" : formatDate(item[col.value]);
        case "choice_single":
            return (item, col) => item[col.value] == null ? "" : formatChoiceValue(col, item[col.value]);
        case "choice_multi":
            return (item, col) => item[col.value] == null ? "" : formatMultiChoiceValue(col, item[col.value]);
        case "project_status":
            return (item, col) => formatProjectStatus(item, col);
        case "client_status":
            return (item, col) => formatClientStatus(item, col);
        case "country_code":
            return (item, col) => {
                if (item[col.value] == null) return "";
                let valObj = appState.countries.find(x => x.code === item[col.value]);
                return valObj == null ? item[col.value] : valObj.name;
            };
        case "user_single":
            return (item, col) => {
                if (item[col.value] === undefined || item[col.value] == null) return "";
                let valObj = userActions.getMemberById(item[col.value]);
                return valObj ? valObj.name : item[col.value];
            };
        case "user_multi":
            return (item, col) => {
                if (item[col.value] === undefined || item[col.value] == null) return "";
                let valObjList = userActions.getMembersByIds(item[col.value]);
                return valObjList.map(x => x.name).join(", ");
            };
        case "article":
            console.error("No format fx for " + field_type);
            return (item, col) => item[col.value] == null ? "" : item[col.value];
        case "client_groups":
            return (item, col) => {
                if (item[col.value] === undefined || item[col.value] == null) return "";
                if (appState.client_groups[col.account_id] === undefined) return item[col.value].join(", ");
                let valObjList = appState.client_groups[col.account_id]
                    .filter(x => item[col.value].indexOf(x.id) >= 0)
                    .map(x => x.name);
                return valObjList.join(", ");
            };
        case "language":
            return (item, col) => {
                if (item[col.value] == null) return "";
                let valObj = appState.languages.find(x => x.code === item[col.value]);
                return valObj == null ? item[col.value] : valObj.name;
            };
        case "client":
            return (item, col) => {
                if (item[col.value] == null) return "";
                if (item[col.value + "_disp"] !== undefined && item[col.value + "_disp"] != null) {
                    return item[col.value + "_disp"].name;
                }
                return item[col.value];
            };
        default:
            if (field_type.startsWith("supplier.")) {
                return (item, col) => {
                    if (!item[col.value]) return "";
                    return item[col.value + "_disp"];
                };
            }
            console.error("No format fx for " + field_type);
            return (item, col) => item[col.value] == null ? "" : item[col.value];
    }
};

export const readCustomFields = (formData, accountId, relatedType) => {
    let groupName = null;
    let data_list = [];
    let sort_order = 100;

    let myItems = []
    if (formData.static_items && formData.static_items.length)
        myItems = myItems.concat(formData.static_items)
    if (formData.items && formData.items.length)
        myItems = myItems.concat(formData.items)

    for (let i = 0; i < myItems.length; i++) {
        let item = myItems[i]
        if (item.row_type === "group") {
            groupName = item.title === "" ? null : item.title
            continue
        }

        data_list.push({
            validation_row_index: i,
            id: isNaN(item.id) && item.id.startsWith("tmp") ? 0 : item.id,
            is_deleted: item.is_deleted,
            account_id: accountId,
            group_name: groupName,
            title: item.title,
            related_type: relatedType,
            name: item.name,
            type: item.type,
            data_column: item.data_column,
            sort_order: sort_order,
            _data: item._data,

            required: item.required,
            is_visible: item.is_visible,
            is_listed: item.is_listed,
            is_editable: item.is_editable,
            is_deletable: item.is_deletable,
            is_sortable: item.is_sortable,
            is_importable: item.is_importable,
        });
        sort_order += 100
    }
    return data_list
}

/**
 Used in Client Edit Form to draw sections and components
 */
export const useCustomFields = (accountId, relatedType, staticParent, columnsCount, parentRelatedType) => {

    let fields = getCustomFieldsByRelatedType(accountId, [relatedType]);
    let dataStruct = {};
    let requiredClassifiers = [];
    for (let field of fields) {
        dataStruct[field.name] = field.type;
        let requiredClassifier = getRequiredClassifierByField(field);
        if (requiredClassifier != null && requiredClassifiers.indexOf(requiredClassifier) === -1)
            requiredClassifiers.push(requiredClassifier);
    }

    let groupNames = Array.unique(
        fields.filter(x => {
            return x.is_visible
        }).map(x => x.group_name), true
    );

    let groupedFields = [];
    let addToEndGroups = []

    for (let group_name of groupNames) {
        let slot = null

        let group_fields = fields.filter(y => {
            return y.is_visible && group_name === (y.group_name || "")
        });

        let mappedFields = group_fields.map(z => {
            return {
                name: z.name,
                field: z,
                readonly: z.is_editable ? 0 : 1,
                component: getFieldComponent(z),
                sizeCssClass: getFieldSizeCssClass(z, columnsCount),
            };
        })

        if (parentRelatedType !== 'client') {
            // Only show client portal for client contacts(not project, suppliers)
            mappedFields = mappedFields.filter(x => x.field.type !== 'cp_rights')
        }

        let currentFields = []

        if (addToEndGroups.length) {
            groupedFields = groupedFields.concat(addToEndGroups)
        }
        addToEndGroups = []

        for (const index in mappedFields) {
            const field = mappedFields[index]

            const isLast = index === String(mappedFields.length - 1)
            let showAdd = field.field.type === "contacts" || field.field.type.startsWith("suppliers.") || field.field.type === "emails"

            if (group_name === '' && relatedType === 'client' && index === '0') {
                slot = 'my-company'
            }

            if (group_name === '' && relatedType === 'client') {
                group_name = $t('Client company')
            } else if (group_name === '') {
                group_name = $t('General info')
            }
            // FIXME :: this needs an actual solution, this is temp fix
            else if (group_name === 'Digitisation of client documents') {
                group_name = $t('Digitisation of client documents')
            }
            else if(group_name === 'Client portal') {
                group_name = $t('Client portal')
            } else if (group_name === 'Documents') {
                group_name = $t('Documents')
            }

            if (showAdd && (index !== '0' || !isLast)) {
                if (currentFields.length) {
                    groupedFields.push({
                        group_name: group_name,
                        fields: currentFields,
                        showAdd: false,
                        isShowAddVisible: false,
                        is_dirty: false,
                        slot
                    });
                }
                currentFields = []

                groupedFields.push({
                    group_name: group_name,
                    fields: [field],
                    showAdd,
                    isShowAddVisible: false,
                    is_dirty: false
                });
                showAdd = false;
                slot = null;
            } else {
                currentFields.push(field);
            }

            if (isLast && currentFields.length) {
                groupedFields.push({
                    group_name: group_name,
                    fields: currentFields,
                    showAdd,
                    isShowAddVisible: false,
                    is_dirty: false,
                    slot
                });

                currentFields = []
            }
        }
    }

    if (addToEndGroups.length) {
        groupedFields = groupedFields.concat(addToEndGroups)
    }

    if (relatedType === 'client') {
        groupedFields.push({
            slot: 'members',
            showAdd: true,
            isShowAddVisible: false,
            group_name: $t('Members'),
            is_dirty: false
        })

        groupedFields.push({
            slot: 'preferences',
            group_name: $t('Preferences'),
            is_dirty: false
        })
    }

    return {
        grouped_fields: groupedFields,
        data_struct: dataStruct,
        required_classifiers: requiredClassifiers
    };
};

export const formatCustomFieldValue = (val, field, obj) => {
    if (val == null || val === "") return "";
    switch (field.type) {
        case "language": {
            let a = val.split("_")
            let lang = get_language_from_code(a[0].toLowerCase())
            if (lang && lang.length === 2 && a.length === 2) {
                lang = get_language_from_code(a[1].toLowerCase())
            }
            return lang
        }
        case "country_code":
            return get_country_from_code(val)
        case "client_groups":
            return obj["group_names"].join(", ");
        case "text":
            return val;
        case "integer":
        case "number":
            return formatNumber(val);
        case "choice_single": {
            if (!field._data || !field._data.choices) return val;
            let c = field._data.choices.find(x => ('' + x.code).toLowerCase() === ('' + val).toLowerCase());
            return c == null ? val : c.name;
        }
        case "choice_multi": {
            if (!field._data || !field._data.choices) return val.join(", ");
            let arr = [];
            for (let i = 0; i < val.length; i++) {
                let c = field._data.choices.find(x => ('' + x.code).toLowerCase() === ('' + val[i]).toLowerCase());
                arr.push(c == null ? val[i] : c.name);
            }
            return arr.join(", ");
        }
        case "date": {
            if (field.name === 'financial_year_end') {
                return formatDateFriendlyNoYear(val, false)
            } else if (field.name === 'birthday') {
                let n = new Date();
                n.setHours(0, 0, 0, 0);
                let d = parseSysDate(val);
                let age = n.getFullYear() - d.getFullYear();
                if (n.getMonth() < d.getMonth() || (n.getMonth() === d.getMonth() && n.getDate() < d.getDate())) age--;
                return "{0} ({1})".format(formatDateCustom(d, CustomFormats.numeric_day_with_month_name), age);
            } else {
                return formatDate(val)
            }
        }
        case "boolean":
            return val ? $t("Yes") : $t("No");
        case "client_status":
            return formatClientStatus(obj)
        default:
            return val;
    }
}